import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Layers } from "lucide-react";
import { RowsEditor } from "@/components/tally/RowsEditor";
import { Field } from "@/components/tally/TallyHeader";
import { STRING_TYPES, newJoint, rowsToJoints, parseTallyFile } from "@/lib/tally";

const btn = "inline-flex items-center gap-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-[#f8fafc] px-3 py-2 mono text-[10px] uppercase tracking-widest transition-colors";

export function JointsEditor({ joints, setJoints, header, running }) {
  const type = STRING_TYPES[header.string_type] || STRING_TYPES.drill;
  const [bulk, setBulk] = useState({ count: "30", length: "31.2" });
  const fileRef = useRef(null);
  const defaults = { kind: type.bulkKind, od: header.string_od, id: header.string_id };
  // Prefetch the spreadsheet parser so import also works offline later.
  useEffect(() => {
    import("xlsx").catch(() => {});
  }, []);

  const addBulk = () => {
    const n = Math.min(parseInt(bulk.count, 10) || 0, 500);
    if (!n || !(parseFloat(bulk.length) > 0)) return toast.error("Enter a joint count and length");
    setJoints([...joints, ...Array.from({ length: n }, () => newJoint({ ...defaults, length: bulk.length }))]);
    toast.success(`Added ${n} joints`);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const imported = rowsToJoints(await parseTallyFile(file), defaults);
      if (!imported.length) return toast.error("No joint lengths found — include a 'Length' column");
      setJoints([...joints, ...imported]);
      toast.success(`Imported ${imported.length} joints from ${file.name}`);
    } catch (err) {
      console.error("Tally import failed", err);
      toast.error("Could not read that file");
    }
  };

  return (
    <div data-testid="tally-joints" className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-24"><Field label="Joints" type="number" value={bulk.count} onChange={(v) => setBulk({ ...bulk, count: v })} testid="tally-bulk-count" /></div>
        <div className="w-28"><Field label="Length each" unit="ft" type="number" value={bulk.length} onChange={(v) => setBulk({ ...bulk, length: v })} testid="tally-bulk-length" /></div>
        <button data-testid="tally-bulk-add" onClick={addBulk} className={btn}><Layers className="w-3.5 h-3.5" /> Bulk add {type.bulkKind}</button>
        <button data-testid="tally-import-btn" onClick={() => fileRef.current?.click()} className={btn}><Upload className="w-3.5 h-3.5" /> Import CSV / Excel</button>
        <input ref={fileRef} data-testid="tally-import-input" type="file" accept=".csv,.xlsx,.xls,text/csv" className="hidden" onChange={onFile} />
      </div>
      <p className="text-[11px] text-[#64748b]">List joints from the bottom up — row 1 is the bit / shoe. Import reads a column named “Length” (optional Description, OD, ID, Type).</p>
      <RowsEditor
        testid="tally-joint"
        rows={joints}
        setRows={setJoints}
        newRow={() => newJoint(defaults)}
        addLabel="Add joint"
        columns={[
          { key: "kind", label: "Type", options: Array.from(new Set([...type.kinds, ...joints.map((j) => j.kind).filter(Boolean)])), width: 130 },
          { key: "desc", label: "Description / Serial", width: 160 },
          { key: "od", label: "OD in", type: "number", width: 70 },
          { key: "id", label: "ID in", type: "number", width: 70 },
          { key: "length", label: "Length ft", type: "number", width: 80 },
        ]}
        renderExtra={{ label: "Running ft", render: (_, i) => running[i]?.running?.toLocaleString() ?? "—" }}
      />
    </div>
  );
}
